import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { agencyDashboardApiService } from '../api/dashboard-api';
import {
  getDashboardDataRequest,
  getDashboardDataSuccess,
  dashboardFailure,
  GetDashboardDataRequest,
  ExtendedDashboardData,
} from './dashboard-slice';

// Saga para obtener datos del dashboard
function* getDashboardDataSaga(
  action: PayloadAction<GetDashboardDataRequest>
): Generator<any, void, any> {
  try {
    // Obtener datos principales del dashboard
    const mainData: any = yield call(agencyDashboardApiService.getDashboardData);

    // Obtener métricas adicionales - si alguna falla, continuamos con null
    let agentsMetrics = null;
    let financialSummary = null;
    let alerts = null;
    let stats7d = null;
    let stats30d = null;
    let leadsTrends = null;
    let performanceMetrics = null;
    let campaignsStats = null;

    try {
      agentsMetrics = yield call(agencyDashboardApiService.getAgentsMetrics);
    } catch (error) {
        console.warn('[agency-dashboard-saga] optional API call failed:', error);
      }

    try {
      financialSummary = yield call(agencyDashboardApiService.getFinancialSummary);
    } catch (error) {
        console.warn('[agency-dashboard-saga] optional API call failed:', error);
      }

    try {
      alerts = yield call(agencyDashboardApiService.getAgencyAlerts);
    } catch (error) {
        console.warn('[agency-dashboard-saga] optional API call failed:', error);
      }

    try {
      stats7d = yield call(() => agencyDashboardApiService.getStatsByPeriod('7d'));
    } catch (error) {
        console.warn('[agency-dashboard-saga] optional API call failed:', error);
      }

    try {
      stats30d = yield call(() => agencyDashboardApiService.getStatsByPeriod('30d'));
    } catch (error) {
        console.warn('[agency-dashboard-saga] optional API call failed:', error);
      }

    try {
      leadsTrends = yield call(agencyDashboardApiService.getLeadsTrends);
    } catch (error) {
        console.warn('[agency-dashboard-saga] optional API call failed:', error);
      }

    try {
      performanceMetrics = yield call(agencyDashboardApiService.getPerformanceMetrics);
    } catch (error) {
        console.warn('[agency-dashboard-saga] optional API call failed:', error);
      }

    try {
      campaignsStats = yield call(agencyDashboardApiService.getCampaignsStats);
    } catch (error) {
        console.warn('[agency-dashboard-saga] optional API call failed:', error);
      }

    // Calcular crecimientos reales comparando períodos (7d vs 30d)
    // Si no hay datos, los crecimientos serán 0 (empresa nueva)
    let propertiesGrowth = 0;
    let agentsGrowth = 0;
    let clientsGrowth = 0;
    const revenueGrowth = mainData.metrics?.revenueGrowth || 0;

    // Calcular crecimiento de propiedades comparando 7d vs 30d
    // Crecimiento = ((promedio7d / promedio30d) - 1) * 100
    if (stats7d && stats30d && stats30d.properties > 0) {
      const avg7dProperties = stats7d.properties / 7; // Promedio diario en 7 días
      const avg30dProperties = stats30d.properties / 30; // Promedio diario en 30 días
      if (avg30dProperties > 0) {
        propertiesGrowth = ((avg7dProperties / avg30dProperties) - 1) * 100;
      }
    } else if (stats30d?.growth !== undefined && stats30d.growth > 0) {
      // Fallback: usar growth del backend si está disponible
      propertiesGrowth = stats30d.growth;
    }

    // Calcular crecimiento de clientes/leads comparando 7d vs 30d
    if (stats7d && stats30d && stats30d.leads > 0) {
      const avg7dLeads = stats7d.leads / 7; // Promedio diario en 7 días
      const avg30dLeads = stats30d.leads / 30; // Promedio diario en 30 días
      if (avg30dLeads > 0) {
        clientsGrowth = ((avg7dLeads / avg30dLeads) - 1) * 100;
      }
    } else if (stats30d?.growth !== undefined && stats30d.growth > 0) {
      // Fallback: usar growth del backend si está disponible
      clientsGrowth = stats30d.growth;
    }

    // Calcular crecimiento de agentes comparando activos/totales vs histórico
    // Por ahora usamos una aproximación: si hay agentes activos y totales, estimamos crecimiento
    if (agentsMetrics && agentsMetrics.totalAgents > 0) {
      // Si no hay histórico, asumimos crecimiento 0 para empresas nuevas
      // En el futuro, cuando haya histórico, compararemos agentes del período anterior
      agentsGrowth = 0; // Empresa nueva sin histórico
    }

    // Crecimiento de ingresos viene del backend (ya calculado)
    // Si no hay datos, será 0

    // Combinar todos los datos
    const dashboardData: ExtendedDashboardData = {
      metrics: mainData.metrics || {},
      recentLeads: mainData.recentLeads || [],
      recentProperties: mainData.recentProperties || [],
      totalLeads: mainData.totalLeads || 0,
      totalProperties: mainData.totalProperties || 0,
      activeProperties: mainData.activeProperties || 0,
      soldProperties: mainData.soldProperties || 0,
      rentedProperties: mainData.rentedProperties || 0,
      // Métricas adicionales
      agentsMetrics: agentsMetrics || undefined,
      financialSummary: financialSummary || undefined,
      alerts: alerts || undefined,
      statsByPeriod: {
        '7d': stats7d || undefined,
        '30d': stats30d || undefined,
      },
      leadsTrends: leadsTrends || undefined,
      performanceMetrics: performanceMetrics || undefined,
      campaignsStats: campaignsStats || undefined,
      // Crecimientos calculados (comparando períodos reales)
      _calculatedGrowth: {
        properties: Math.round(propertiesGrowth * 100) / 100, // Redondear a 2 decimales
        agents: Math.round(agentsGrowth * 100) / 100,
        clients: Math.round(clientsGrowth * 100) / 100,
        revenue: Math.round(revenueGrowth * 100) / 100,
      },
    };

    yield put(getDashboardDataSuccess(dashboardData));
  } catch (error: any) {
    yield put(
      dashboardFailure(
        error.response?.data?.message || 'Error al obtener los datos del dashboard'
      )
    );
  }
}

// Watcher saga
export function* watchGetDashboardData() {
  yield takeLatest(getDashboardDataRequest.type, getDashboardDataSaga);
}

// Root saga para dashboard
export function* dashboardSaga() {
  yield takeLatest(getDashboardDataRequest.type, getDashboardDataSaga);
}

